import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { nextId } from '../../utils/ids';
import { Portal } from '../Portal/Portal';
import { Toast, type ToastAction, type ToastTone } from './Toast';
import { DsI18nProvider, useDsT, useDsTranslator, type DsTranslator } from '../../services/i18n';

export interface ToastOptions {
  id?: string;
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** Auto-dismiss after ms (default 5000). `null` — or `0` — keeps the toast until it is dismissed. */
  duration?: number | null;
  action?: ToastAction;
  dismissible?: boolean;
  /** Pending state (a promise-bound toast before it settles). */
  loading?: boolean;
}

export interface ToastItem extends ToastOptions {
  id: string;
  tone: ToastTone;
  duration: number | null;
}

type Shortcut = (title: ReactNode, options?: Omit<ToastOptions, 'title' | 'tone'>) => string;

/** Message for one outcome of a promise-bound toast: a node, or a function of the value/error. */
export type ToastPromiseMessage<T> = ReactNode | ((value: T) => ReactNode);

export interface ToastPromiseOptions<T> {
  loading: ReactNode;
  success: ToastPromiseMessage<T>;
  error: ToastPromiseMessage<unknown>;
  /** Reuse an existing toast id so the swap happens in place. */
  id?: string;
  /** Auto-dismiss for the success toast (errors stay until dismissed). */
  duration?: number | null;
  description?: ReactNode;
}

export interface ToastFn {
  (options: ToastOptions): string;
  success: Shortcut;
  error: Shortcut;
  warning: Shortcut;
  info: Shortcut;
  /**
   * Shows a loading toast and swaps it, in place, for the success or error result.
   * Returns the original promise so callers can keep awaiting it.
   */
  promise: <T>(promise: Promise<T>, options: ToastPromiseOptions<T>) => Promise<T>;
}

export interface ToastContextValue {
  toast: ToastFn;
  dismiss: (id?: string) => void;
  toasts: ToastItem[];
  /** Set by {@link ToastI18nBridge}: the translator of the app's tree, which the toast portal sits above. */
  setTranslator?: (translate: DsTranslator | null) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface ToastProviderProps {
  children: ReactNode;
  position?: ToastPosition;
  /** Maximum toasts shown at once (default 3); the rest queue. */
  max?: number;
  defaultDuration?: number;
  /** Collapse the visible toasts into an offset stack until the region is hovered (default true). */
  stacked?: boolean;
}

const insetClass: Record<ToastPosition, string> = {
  'top-left': 'top-4 start-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'top-right': 'top-4 end-4',
  'bottom-left': 'bottom-4 start-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 end-4',
};

const alignClass: Record<ToastPosition, { flex: string; grid: string }> = {
  'top-left': { flex: 'items-start', grid: 'justify-items-start' },
  'top-center': { flex: 'items-center', grid: 'justify-items-center' },
  'top-right': { flex: 'items-end', grid: 'justify-items-end' },
  'bottom-left': { flex: 'items-start', grid: 'justify-items-start' },
  'bottom-center': { flex: 'items-center', grid: 'justify-items-center' },
  'bottom-right': { flex: 'items-end', grid: 'justify-items-end' },
};

interface TimedToastProps {
  item: ToastItem;
  onDismiss: () => void;
  depth: number;
  collapsed: boolean;
  from: 'top' | 'bottom';
}

function TimedToast({ item, onDismiss, depth, collapsed, from }: TimedToastProps) {
  const remaining = useRef(item.duration);
  const startedAt = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holds = useRef(0);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const start = useCallback((duration: number | null) => {
    if (duration == null || duration <= 0) return;
    startedAt.current = Date.now();
    timer.current = setTimeout(() => dismissRef.current(), duration);
  }, []);
  const pause = () => {
    holds.current += 1;
    if (startedAt.current != null && remaining.current != null) remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    startedAt.current = null;
    clear();
  };
  const resume = () => {
    holds.current = Math.max(0, holds.current - 1);
    if (holds.current === 0 && !timer.current) start(remaining.current);
  };

  // Re-arms when the toast is swapped in place (the promise variant changes `duration`).
  useEffect(() => {
    clear();
    remaining.current = item.duration;
    holds.current = 0;
    start(item.duration);
    return clear;
  }, [item.id, item.duration, start]);

  return (
    <Toast
      title={item.title}
      description={item.description}
      tone={item.tone}
      action={item.action}
      dismissible={item.dismissible}
      loading={item.loading}
      onDismiss={onDismiss}
      data-toast-id={item.id}
      stackDepth={depth}
      stackCollapsed={collapsed}
      stackFrom={from}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    />
  );
}

function resolveMessage<T>(message: ToastPromiseMessage<T>, value: T): ReactNode {
  return typeof message === 'function' ? (message as (v: T) => ReactNode)(value) : message;
}

/** Toasts visible at once; anything beyond this queues until one is dismissed. */
export const TOAST_MAX_VISIBLE = 3;

export function ToastProvider({ children, position = 'bottom-right', max = TOAST_MAX_VISIBLE, defaultDuration = 5000, stacked = true }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const dsT = useDsT();
  const [translator, setTranslatorState] = useState<DsTranslator | null>(null);
  const setTranslator = useCallback((next: DsTranslator | null) => setTranslatorState(() => next), []);
  const [expanded, setExpanded] = useState(false);

  const dismiss = useCallback((id?: string) => {
    setToasts((prev) => (id === undefined ? [] : prev.filter((t) => t.id !== id)));
  }, []);

  const toast = useMemo<ToastFn>(() => {
    const push = (options: ToastOptions): string => {
      const id = options.id ?? nextId('toast');
      // `undefined` takes the default; `null` (and 0) means "until dismissed", which is what errors default to.
      const item: ToastItem = { ...options, id, tone: options.tone ?? 'neutral', duration: options.duration === undefined ? defaultDuration : options.duration };
      setToasts((prev) => {
        const index = prev.findIndex((t) => t.id === id);
        // Replacing in place is what makes `toast.promise` swap rather than re-queue.
        if (index >= 0) {
          const next = prev.slice();
          next[index] = item;
          return next;
        }
        return [...prev, item];
      });
      return id;
    };
    const fn = push as ToastFn;
    fn.success = (title, o) => push({ ...o, title, tone: 'success' });
    fn.error = (title, o) => push({ ...o, title, tone: 'danger', duration: o?.duration === undefined ? null : o.duration });
    fn.warning = (title, o) => push({ ...o, title, tone: 'warning' });
    fn.info = (title, o) => push({ ...o, title, tone: 'info' });
    fn.promise = <T,>(promise: Promise<T>, options: ToastPromiseOptions<T>): Promise<T> => {
      const id = options.id ?? nextId('toast');
      push({ id, title: options.loading, description: options.description, tone: 'neutral', duration: null, loading: true, dismissible: false });
      promise.then(
        (value) => {
          push({ id, title: resolveMessage(options.success, value), tone: 'success', duration: options.duration ?? defaultDuration, dismissible: true });
        },
        (error: unknown) => {
          push({ id, title: resolveMessage(options.error, error), tone: 'danger', duration: null, dismissible: true });
        },
      );
      return promise;
    };
    return fn;
  }, [defaultDuration]);

  const value = useMemo(() => ({ toast, dismiss, toasts, setTranslator }), [toast, dismiss, toasts, setTranslator]);
  const visible = toasts.slice(0, max);
  const fromBottom = position.startsWith('bottom');
  const collapsed = stacked && !expanded && visible.length > 1;
  const align = alignClass[position];

  // The region and its toasts render above the app's i18n provider; the bridge hands its translator up (W12).
  const portalContent = () => (
        <Portal>
          <div
            role="region"
            aria-label={translator?.('ds.toast.region') ?? dsT('ds.toast.region', 'Notifications')}
            aria-live="polite"
            data-expanded={collapsed ? 'false' : 'true'}
            onMouseEnter={() => setExpanded(true)}
            onMouseLeave={() => setExpanded(false)}
            onFocusCapture={() => setExpanded(true)}
            onBlurCapture={() => setExpanded(false)}
            style={{ zIndex: 'var(--zo-z-toast)' }}
            className={cn(
              'zo-toast-region pointer-events-none fixed',
              insetClass[position],
              collapsed
                ? cn('grid [&>*]:[grid-area:1/1]', align.grid)
                : cn('flex gap-2', fromBottom ? 'flex-col-reverse' : 'flex-col', align.flex),
            )}
          >
            {visible.map((item, index) => (
              <TimedToast
                key={item.id}
                item={item}
                onDismiss={() => dismiss(item.id)}
                depth={fromBottom ? visible.length - 1 - index : index}
                collapsed={collapsed}
                from={fromBottom ? 'bottom' : 'top'}
              />
            ))}
          </div>
        </Portal>
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {translator ? <DsI18nProvider translate={translator}>{portalContent()}</DsI18nProvider> : portalContent()}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

/**
 * Rendered once inside the app's `DsI18nProvider`: gives the toast region — mounted above it — the same translator, so
 * the region's name and every toast's own words ("Dismiss") follow the viewer's language.
 */
export function ToastI18nBridge(): null {
  const translate = useDsTranslator();
  const ctx = useContext(ToastContext);
  const setTranslator = ctx?.setTranslator;
  useEffect(() => {
    setTranslator?.(translate);
  }, [setTranslator, translate]);
  return null;
}
